/**
 * 接口层异常观测（X1）+ 全站故障检测（X2）
 *
 * ── 背景 ──────────────────────────────────────────────────────────────────
 * 2026-09-04 生产事故：SDK 开启 trace 后注入 baggage，后端 CORS 未允许 →
 * 全站 /v1/* 请求 preflight 失败 → 请求根本发不出去。而当时看板上**完全看不到**，
 * 因为 `request.ts` 的 `error.request` 分支只有一行 console.error，零埋点。
 *
 * ── 设计原则（务必守住）──────────────────────────────────────────────────
 * 1. **纯前端闭环**：不依赖任何其他端配合。所有增强手段（Resource Timing / no-cors
 *    探测）都是「尽力而为」——读不到或失败一律优雅降级，绝不影响主流程，更不会
 *    因为没有其他端配合而崩溃。
 * 2. **零业务影响**：全程 try/catch；不改请求、不注入任何请求头、不阻塞 Promise。
 * 3. **不增加常态请求**：no-cors 探测仅在异常态触发，且按 host 节流（默认同一 host
 *    冷却 60s），绝不会形成请求风暴。
 * 4. **只在异常态上报**：符合「正常态零上报」原则；用户主动取消不计。
 */

import type { AxiosError } from 'axios';
import { reportCustomEvent } from '@/config/apm';
import { dumpErrorDetail } from './snapshot';
import { mark } from './window';

// ─── 配置项（集中管理，便于按线上情况调整）────────────────────────────────
const CONFIG = {
  /** 全站故障：滑动窗口长度 */
  outageWindowMs: 60_000,
  /** 全站故障：窗口内最小样本数（样本太少不判定，避免误报） */
  outageMinSamples: 10,
  /** 全站故障：窗口失败率阈值 */
  outageFailRate: 0.5,
  /** 全站故障：连续失败次数阈值（达到即判定，响应更快） */
  outageConsecutive: 10,
  /** 全站故障恢复：连续成功次数 */
  outageRecoverConsecutive: 5,
  /** CORS 探测冷却（同一 host，毫秒） */
  probeCooldownMs: 60_000,
  /** CORS 探测超时（毫秒） */
  probeTimeoutMs: 5_000,
} as const;

/** X1 事件名 */
const EVENT_ABNORMAL = 'api_request_abnormal';
/** X2 事件名 */
const EVENT_OUTAGE = 'api_global_outage';

// ─── 工具：URL 归一化（防高基数）───────────────────────────────────────────
// 原始 url 绝不能进 categories：每个请求自成一类，分组形同虚设。
const normalizeApiGroup = (url: string | undefined): string => {
  if (!url) return 'unknown';
  try {
    // 去掉 query 与 hash，只留路径
    const path = url.split('?')[0].split('#')[0];
    return (
      path
        // UUID
        .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '/:uuid')
        // 纯数字 id
        .replace(/\/\d+(?=\/|$)/g, '/:id')
        // 长哈希 / ULID（16 位以上字母数字）
        .replace(/\/[0-9a-zA-Z]{16,}(?=\/|$)/g, '/:hash')
    );
  } catch {
    return 'unknown';
  }
};

// ─── 工具：x-traceid 提取（响应头溯源）─────────────────────────────────────
// 后端在每个响应里返回 X-Traceid 用于日志溯源。提取出来，错误上报时随详情日志
// 带上，方便排查时直接拿这个 id 找后端日志。
//
// 设计要点：
//  1. 用 WeakMap<config, traceId> 缓存：axios 同一请求的 config 是同一引用，响应
//     拦截器与错误拦截器都拿得到；config 被 GC 时自动清理，不污染 axios。
//  2. 大小写不敏感读取响应头：HTTP header name 不区分大小写，axios 1.x 默认
//     lowercase，但保险起见三种写法都试。
//  3. traceId 不进 categories：每次请求都不同，是高基数 → 走 dumpErrorDetail 的
//     content，单条排查时取出。
const TRACE_HEADER = 'x-traceid';
const traceIdCache = new WeakMap<object, string>();

const readHeader = (headers: unknown, name: string): string | undefined => {
  if (!headers || typeof headers !== 'object') return undefined;
  const h = headers as Record<string, unknown>;
  const lname = name.toLowerCase();
  const v = h[lname] ?? h[name] ?? h[name.toUpperCase()];
  if (Array.isArray(v)) return typeof v[0] === 'string' ? v[0] : undefined;
  return typeof v === 'string' ? v : undefined;
};

/** 从响应头提取 traceId 并缓存到 config。返回提取到的值。 */
export function extractTraceId(headers: unknown, config: object | undefined): string | undefined {
  const id = readHeader(headers, TRACE_HEADER);
  if (!id) return undefined;
  if (config) {
    try {
      traceIdCache.set(config, id);
    } catch {
      /* 忽略 WeakMap 异常（极罕见） */
    }
  }
  return id;
}

/** 从缓存读取 traceId（错误拦截器调用）。 */
export function getStoredTraceId(config: object | undefined): string | undefined {
  if (!config) return undefined;
  try {
    return traceIdCache.get(config);
  } catch {
    return undefined;
  }
}

// ─── 第一层：基于 axios error 的基础分类 ────────────────────────────────────
export type RequestErrorClass =
  | 'canceled' // 用户主动取消 —— 正常交互，不上报
  | 'timeout'
  | 'offline'
  | 'network' // CORS 拦截 / DNS 失败 / 连接被拒（无法细分时的兜底）
  | 'cors' // 经探测确认为跨域策略拦截
  | 'nginx_blocked' // 响应是 HTML 而非 JSON（被网关/ng 拦截的典型特征）
  | 'empty_204' // 204 空响应
  | 'parse_error'
  | 'other';

/** 安全读取 navigator.onLine（某些环境可能不存在） */
const isOnline = (): boolean => {
  try {
    return typeof navigator === 'undefined' ? true : navigator.onLine !== false;
  } catch {
    return true;
  }
};

/**
 * 第一层分类：仅依赖 axios error 自身字段，零外部依赖，必定可用。
 */
export function classifyRequestError(error: AxiosError | null | undefined): RequestErrorClass {
  try {
    if (!error) return 'other';

    // 用户主动取消：正常交互，不上报
    const code = (error as { code?: string }).code;
    if (code === 'ERR_CANCELED' || error.name === 'CanceledError') return 'canceled';

    // 超时
    if (code === 'ECONNABORTED' || code === 'ETIMEDOUT') return 'timeout';
    if (error.message?.toLowerCase().includes('timeout')) return 'timeout';

    // 有响应 → 不是网络层问题，交给调用方按状态码/格式判断
    if (error.response) return 'other';

    // 无响应：请求未到达后端
    if (!isOnline()) return 'offline';
    return 'network';
  } catch {
    return 'other';
  }
}

/**
 * 检测是否被网关/nginx 拦截：2xx 但响应体是 HTML（而非预期 JSON）。
 * 前端可精确识别，无需任何其他端配合。
 */
export function isHtmlErrorPage(response: { headers?: Record<string, unknown>; data?: unknown }): boolean {
  try {
    const contentType =
      (response?.headers?.['content-type'] as string | undefined) ??
      (response?.headers?.['Content-Type'] as string | undefined) ??
      '';
    if (contentType.toLowerCase().includes('text/html')) return true;
    // 兜底：content-type 缺失但 body 是 HTML 字符串
    return typeof response?.data === 'string' && /^\s*<!DOCTYPE html|^\s*<html/i.test(response.data);
  } catch {
    return false;
  }
}

// ─── 第二层：Resource Timing 推断失败阶段 ───────────────────────────────────
/**
 * 尽力而为的增强：利用 PerformanceResourceTiming 推断失败发生在哪个阶段。
 *
 * ⚠️ 降级说明（不影响正确性）：
 *  - 跨域资源若服务端未返回 `Timing-Allow-Origin`，详细阶段字段（domainLookupEnd /
 *    connectEnd 等）会被置 0 —— 此时本函数自动跳过阶段判断，返回 null，由基础分类兜底。
 *  - 若浏览器不支持该 API，同样返回 null。
 *  - **这一切都不需要任何其他端配合**，读不到只是少一点精度，不会出错。
 *
 * @returns 'not_sent' 错误发生时 buffer 中查无该 URL 的条目。
 *          ⚠️ **弱信号，仅供参考**：既可能是真实请求压根没发出（如 preflight 被拦），
 *          也可能是 PerformanceResourceTiming 条目尚未写入 buffer（规范不保证
 *          entry 写入先于 fetch/XHR 的 error 回调）。**不得单独据此定性为 CORS**，
 *          只能与 no-cors 探测结果、错误信息等其他证据相互印证。
 *          'dns' | 'connect' | 'response' 失败阶段（仅在同源或有 TAO 时可信）
 *          null 无法判断（降级）
 */
export function inferFailureStage(url: string | undefined): 'not_sent' | 'dns' | 'connect' | 'response' | null {
  try {
    if (!url || typeof performance === 'undefined' || !performance.getEntriesByName) return null;

    const entries = performance.getEntriesByName(url, 'resource');
    if (!entries.length) {
      // 完全没有 timing 条目：真实请求压根没有发出（常见于 preflight 被拦）
      return 'not_sent';
    }

    const t = entries[entries.length - 1] as PerformanceResourceTiming;

    // 以下字段在跨域且无 Timing-Allow-Origin 时为 0 —— 此时不可信，直接降级
    const hasDetail = t.domainLookupEnd > 0 || t.connectEnd > 0 || t.responseStart > 0;
    if (!hasDetail) return null;

    if (t.domainLookupEnd === 0) return 'dns';
    if (t.connectEnd === 0) return 'connect';
    if (t.responseStart === 0) return 'response';
    return 'response';
  } catch {
    return null;
  }
}

// ─── 第三层：no-cors 探测（精确区分 CORS vs 网络不可达）────────────────────
// 仅在异常态调用，且按 host 节流。失败 / 不支持一律降级为原分类，绝不抛错。
const probeState = new Map<string, number>(); // host -> lastProbeAt

const hostOf = (url: string): string => {
  try {
    return new URL(url, window.location.origin).host;
  } catch {
    return 'unknown';
  }
};

/**
 * 用 no-cors 模式探测服务器可达性。
 * no-cors 不做 CORS 检查：探测成功 ⇒ 服务器可达 ⇒ 是 CORS 策略问题；
 * 探测失败 ⇒ 服务器不可达 ⇒ 网络 / DNS 问题。
 *
 * 纯前端行为，不需要任何其他端配合；失败也只影响这一个分类的精度。
 */
export async function probeReachability(url: string | undefined): Promise<'reachable' | 'unreachable' | 'unknown'> {
  try {
    if (!url || typeof fetch === 'undefined') return 'unknown';

    const host = hostOf(url);
    const now = Date.now();
    const last = probeState.get(host) ?? 0;
    if (now - last < CONFIG.probeCooldownMs) return 'unknown'; // 冷却中，不重复探测
    probeState.set(host, now);

    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), CONFIG.probeTimeoutMs) : null;

    await fetch(url, {
      method: 'HEAD',
      mode: 'no-cors',
      cache: 'no-store',
      ...(controller ? { signal: controller.signal } : {}),
    });

    if (timer) clearTimeout(timer);
    return 'reachable';
  } catch {
    // 探测本身失败（含超时、断网、DNS）→ 视为不可达
    return 'unreachable';
  }
}

// ─── X1 主入口：上报接口异常 ────────────────────────────────────────────────
export interface ReportRequestAbnormalOptions {
  url?: string;
  method?: string;
  durationMs?: number;
  status?: number;
  errorClass: RequestErrorClass;
  /** 是否异步做 CORS 探测以细化分类（默认 false，避免额外请求；调用方可按需开启） */
  withProbe?: boolean;
  detail?: Record<string, unknown>;
  /**
   * 后端响应头里的 x-traceid（后端用于日志溯源）
   * 走 content（不进 categories）—— 高基数字段，每条都不同，进维度会污染看板
   */
  traceId?: string;
}

/** 已上报指纹，用于短时去重，避免同一故障刷屏 */
const recentReports = new Map<string, number>();
const DEDUPE_WINDOW_MS = 10_000;
/** 去重表上限：key 为「归一化接口 + 错误类」，正常有限；但仍设上限兜底，
 *  防止出现未被归一化覆盖的动态路径时无限累积。 */
const RECENT_REPORTS_MAX = 500;

export function reportRequestAbnormal(options: ReportRequestAbnormalOptions): void {
  try {
    const { url, method, durationMs, status, errorClass, detail, traceId } = options;

    // 用户主动取消：正常交互，不上报
    if (errorClass === 'canceled') return;

    const apiGroup = normalizeApiGroup(url);
    const stage = inferFailureStage(url);

    // 短时去重（同 group + 同 class）
    const key = `${apiGroup}|${errorClass}`;
    const now = Date.now();
    const last = recentReports.get(key) ?? 0;
    if (now - last < DEDUPE_WINDOW_MS) return;

    // 控制 Map 规模：超过上限时先清理已过期的指纹（超出去重窗口即无意义）
    if (recentReports.size >= RECENT_REPORTS_MAX) {
      for (const [k, t] of recentReports) {
        if (now - t >= DEDUPE_WINDOW_MS) recentReports.delete(k);
      }
      // 仍超限（极端情况）→ 整体清空，宁可短暂失去去重也不让内存膨胀
      if (recentReports.size >= RECENT_REPORTS_MAX) recentReports.clear();
    }
    recentReports.set(key, now);

    reportCustomEvent(
      EVENT_ABNORMAL,
      {
        count: 1,
        ...(typeof durationMs === 'number' ? { duration: Math.round(durationMs) } : {}),
        ...(typeof status === 'number' ? { status } : {}),
      },
      {
        errorClass,
        apiGroup,
        // stage 仅在可判断时携带，读不到就不塞维度（避免 unknown 污染）
        ...(stage ? { stage } : {}),
        hasResponse: status !== undefined && status > 0 ? '1' : '0',
        online: isOnline() ? '1' : '0',
        method: (method ?? 'unknown').toLowerCase(),
      }
    );

    // 高基数详情走日志，供单条排查（url / message / traceId 等绝不能进 categories）
    dumpErrorDetail(EVENT_ABNORMAL, {
      url,
      method,
      status,
      errorClass,
      stage,
      // traceId 是后端给每条请求分配的唯一 ID，高基数 → 走 content（不进 categories）
      // 排查时直接拿这个 ID 找后端日志，链路清晰
      ...(traceId ? { traceId } : {}),
      ...(detail ?? {}),
    });

    // 写入环形缓冲：断网时 APM 上报也发不出去，靠下次启动补报
    mark('api.error', `${errorClass} ${apiGroup}`);

    // 可选的异步精细探测：不阻塞主流程，结果仅用于追加一条更精确的日志
    if (options.withProbe && errorClass === 'network') {
      void probeReachability(url).then((reach) => {
        if (reach === 'unknown') return;
        const refined: RequestErrorClass = reach === 'reachable' ? 'cors' : 'network';
        if (refined === 'cors') {
          dumpErrorDetail(EVENT_ABNORMAL, { url, refinedErrorClass: 'cors', note: 'no-cors 探测可达，判定为跨域策略拦截' });
        }
      });
    }
  } catch {
    // 埋点上报失败绝不影响业务流程
  }
}

// ─── X2：全站故障检测（纯前端，基于业务流量自身统计，零新增请求）────────────
/**
 * 说明：不采用主动心跳探测（那会增加业务请求，且可能需要后端提供健康检查接口）。
 * 改为统计「真实业务请求」的成败，在不产生任何额外流量的前提下判断全站可用性。
 */
interface WindowStat {
  total: number;
  failed: number;
  startedAt: number;
}

let stat: WindowStat = { total: 0, failed: 0, startedAt: Date.now() };
let consecutiveFail = 0;
let consecutiveOk = 0;
let outageActive = false;

/** 请求发起时调用（用于统计总数） */
export function recordRequestStart(): void {
  try {
    stat.total += 1;
  } catch {
    /* 忽略 */
  }
}

/** 请求结束时调用：ok=true 成功，false 失败 */
export function recordRequestSettled(ok: boolean): void {
  try {
    if (ok) {
      consecutiveFail = 0;
      consecutiveOk += 1;
    } else {
      stat.failed += 1;
      consecutiveOk = 0;
      consecutiveFail += 1;
    }
    evaluateOutage();
  } catch {
    /* 忽略 */
  }
}

function evaluateOutage(): void {
  const now = Date.now();

  // 快速通道：连续失败达到阈值立即判定（不必等窗口结束）
  if (!outageActive && consecutiveFail >= CONFIG.outageConsecutive) {
    enterOutage('consecutive_failures');
    stat = { total: 0, failed: 0, startedAt: now };
    return;
  }

  // 窗口通道：窗口内样本足够时按失败率判定
  if (now - stat.startedAt >= CONFIG.outageWindowMs) {
    if (!outageActive && stat.total >= CONFIG.outageMinSamples) {
      const rate = stat.failed / stat.total;
      if (rate >= CONFIG.outageFailRate) enterOutage('failure_rate');
    }
    stat = { total: 0, failed: 0, startedAt: now };
  }

  // 恢复判定
  if (outageActive && consecutiveOk >= CONFIG.outageRecoverConsecutive) {
    outageActive = false;
    reportCustomEvent(EVENT_OUTAGE, { count: 1 }, { state: 'recovered' });
  }
}

function enterOutage(reason: string): void {
  outageActive = true;
  reportCustomEvent(EVENT_OUTAGE, { count: 1 }, { state: 'outage', reason, online: isOnline() ? '1' : '0' });
  dumpErrorDetail(EVENT_OUTAGE, { reason, online: isOnline() });
}

/** 供测试/调试读取当前状态（不影响生产逻辑） */
export const __getOutageStat = () => ({
  ...stat,
  consecutiveFail,
  consecutiveOk,
  outageActive,
});
